/* Order tracker core: carrier event rewrite + progress logic. One file for two runtimes:
   the browser (window.TrackCore, used by track.js in preview mode) and the Cloudflare Worker (tracker-worker/src,
   bundled by wrangler) so both show the same words for the same events.

   Truthfulness rules (do not loosen):
   - A step lights only when a real event or a real Shopify timestamp supports it. No virtual events, no timers.
   - Every line keeps the carrier's own English wording (17TRACK's English translation, or the raw text when it has no
     CJK characters) one tap away. Nothing in the result carries CJK characters: Chinese raw text is used only for
     matching, never returned. A Chinese event with no English wording and no rule reads as a generic line from the
     17TRACK stage ("Update from the carrier."), never as an invented specific event.
   - Gaps are reported as gaps ("No new scan since ...") with the real date; nothing is back-filled.
   - The ETA is a window from the real ship date plus the store's stated delivery window, labelled as an estimate.
   Pure functions, no I/O. Rules come from theme/assets/track-rewrites.json, passed in by the caller. */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module && module.exports) module.exports = api;
  if (root) root.TrackCore = api;
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";
  var DAY = 86400000;
  /* CJK: Hangul Jamo, CJK radicals, Kangxi, ideographic description, CJK symbols and punctuation, Hiragana, Katakana,
     Bopomofo, Hangul compatibility, Kanbun, CJK strokes, Katakana extensions, enclosed CJK, CJK compatibility,
     Extension A, unified ideographs, Yi, Hangul Jamo extended, Hangul syllables, compatibility ideographs, vertical forms,
     CJK compatibility forms, halfwidth and fullwidth forms, and the supplementary ideographic planes (surrogate pairs). */
  var CJK_CLASS = "\u1100-\u11FF\u2E80-\u2FDF\u2FF0-\u303F\u3040-\u30FF\u3100-\u312F\u3130-\u318F\u3190-\u31FF\u3200-\u32FF" +
    "\u3300-\u33FF\u3400-\u4DBF\u4E00-\u9FFF\uA000-\uA4CF\uA960-\uA97F\uAC00-\uD7FF\uF900-\uFAFF\uFE10-\uFE1F\uFE30-\uFE4F\uFF00-\uFFEF";
  var CJK = new RegExp("[" + CJK_CLASS + "]|[\uD840-\uD8BF][\uDC00-\uDFFF]");
  var CJK_ALL = new RegExp("(?:[" + CJK_CLASS + "]|[\uD840-\uD8BF][\uDC00-\uDFFF])+", "g");
  function hasCJK(s) { return CJK.test(String(s == null ? "" : s)); }
  /* Remove every CJK run, then tidy the spaces and the punctuation left at the edges. */
  function stripCJK(s) {
    var t = String(s == null ? "" : s);
    if (!CJK.test(t)) return t;
    return t.replace(CJK_ALL, " ").replace(/\s+/g, " ").replace(/\(\s*\)/g, "").replace(/\s+([,.;:!?)])/g, "$1")
      .replace(/^[\s,.;:!?)\-]+/, "").replace(/[\s,;:(\-]+$/, "").trim();
  }
  /* Deep copy with every string stripped of CJK: the last step before anything leaves this module or the worker. */
  function scrub(v) {
    if (typeof v === "string") return stripCJK(v);
    if (Array.isArray(v)) return v.map(scrub);
    if (v && typeof v === "object") { var o = {}; Object.keys(v).forEach(function (k) { o[k] = scrub(v[k]); }); return o; }
    return v;
  }
  /* Truthful generic lines for events that match no rule and have no English wording: they say only what the
     17TRACK stage or sub-status says, nothing more specific. */
  var GENERIC = {
    InfoReceived: "The carrier has the shipment details.",
    PickedUp: "In the carrier's hands.",
    Departure: "Scanned at a carrier facility.",
    Arrival: "Scanned at a carrier facility.",
    AvailableForPickup: "Ready for pickup at a carrier location.",
    OutForDelivery: "Out for delivery.",
    Delivered: "Delivered.",
    Returning: "Being returned to the sender. Please contact us and we will sort it out.",
    Returned: "Returned to the sender. Please contact us and we will sort it out.",
  };
  var GENERIC_INTL = "Scanned at a carrier facility.";
  var GENERIC_PROBLEM = "The carrier reported a problem. We are checking on it.";
  var GENERIC_ATTEMPT = "Delivery was attempted. Contact us if you need help with redelivery.";
  var GENERIC_NONE = "Update from the carrier.";
  function genericLine(ev, abroad) {
    var sub = String(ev.subStatus || "");
    if (/^DeliveryFailure/.test(sub)) return GENERIC_ATTEMPT;
    if (/^Exception/.test(sub)) return GENERIC_PROBLEM;
    if ((ev.stage === "Departure" || ev.stage === "Arrival") && abroad) return GENERIC_INTL;
    return (ev.stage && GENERIC[ev.stage]) || GENERIC_NONE;
  }
  var STEP_LABELS = { confirmed: "Order confirmed", processing: "Processing", shipped: "Shipped", in_transit: "In transit", out_for_delivery: "Out for delivery", delivered: "Delivered" };

  function compile(rules) {
    var steps = rules.steps || Object.keys(STEP_LABELS);
    var idx = {};
    steps.forEach(function (s, i) { idx[s] = i; });
    var compiled = (rules.rules || []).map(function (r) {
      return {
        id: r.id, kind: r.kind || "ok", step: r.step, leg: r.leg || null, message: r.message, headline: r.headline || null,
        subs: (r.match || []).map(function (m) { return String(m).toLowerCase(); }),
        re: r.regex ? new RegExp(r.regex, "i") : null,
      };
    });
    return { steps: steps, idx: idx, rules: compiled, stageSteps: rules.stage_steps || {}, places: rules.places || {},
             intlPlace: rules.international_place || "Carrier facility", carriers: rules.carriers || {},
             carrierAliases: rules.carrier_aliases || {}, carrierDisplay: rules.carrier_display_name || "",
             carrierNameRe: carrierNameRe(rules),
             labels: Object.assign({}, STEP_LABELS, rules.step_labels || {}) };
  }

  function clean(text) {
    return String(text || "").replace(/\s+/g, " ").replace(/\s*[,;:]\s*$/, "").trim();
  }

  function matchRule(c, text) {
    var t = String(text || "").toLowerCase();
    if (!t) return null;
    for (var i = 0; i < c.rules.length; i++) {
      var r = c.rules[i];
      if (r.re && r.re.test(t)) return r;
      for (var k = 0; k < r.subs.length; k++) if (r.subs[k] && t.indexOf(r.subs[k]) !== -1) return r;
    }
    return null;
  }

  /* English only: a location with CJK becomes the mapped city ("Shenzhen, China"), else its Latin remainder, else
     "Carrier facility". Nothing is relocated. The page shows a location on the timeline row only when isUSLocation()
     says it is in the United States; any other location stays one tap away under "Show details". */
  function friendlyLocation(c, loc) {
    var s = clean(loc);
    if (!s) return "";
    if (!CJK.test(s)) return s;
    var keys = Object.keys(c.places);
    for (var i = 0; i < keys.length; i++) if (s.indexOf(keys[i]) !== -1) return c.places[keys[i]];
    var rest = clean(stripCJK(s));
    return /[A-Za-z]{2,}/.test(rest) ? rest : c.intlPlace;
  }

  /* True when a location string is in the United States: a US country token ("LOS ANGELES, US", "Reno, USA",
     "Chicago, United States"), a state after a comma ("Los Angeles, CA", "Henderson, Nevada"), a state before a ZIP
     ("HENDERSON, NV 89052") or a USPS facility name ("QUEENS NY DISTRIBUTION CENTER"). Anything else, including the
     "Carrier facility" fallback and an empty string, is false. Display only: it never changes what an event says. */
  var US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
  var US_STATE_NAMES = ["alabama", "alaska", "arizona", "arkansas", "california", "colorado", "connecticut", "delaware", "district of columbia",
    "florida", "georgia", "hawaii", "idaho", "illinois", "indiana", "iowa", "kansas", "kentucky", "louisiana", "maine", "maryland",
    "massachusetts", "michigan", "minnesota", "mississippi", "missouri", "montana", "nebraska", "nevada", "new hampshire", "new jersey",
    "new mexico", "new york", "north carolina", "north dakota", "ohio", "oklahoma", "oregon", "pennsylvania", "puerto rico", "rhode island",
    "south carolina", "south dakota", "tennessee", "texas", "utah", "vermont", "virginia", "washington", "west virginia", "wisconsin", "wyoming"];
  var ST = "(?:" + US_STATES.join("|") + ")";
  var US_COUNTRY_RE = /(?:^|[,\s])(?:US|USA|U\.S\.A?\.?|United States(?: of America)?)\s*$/i;
  var US_STATE_TAIL_RE = new RegExp(",\\s*" + ST + "\\.?(?:\\s+\\d{5}(?:-\\d{4})?)?\\s*$");
  var US_STATE_ZIP_RE = new RegExp("(?:^|[\\s,])" + ST + "\\s+\\d{5}(?:-\\d{4})?\\b");
  var US_FACILITY_RE = new RegExp("(?:^|\\s)" + ST + "\\s+(?:NETWORK\\s+)?(?:DISTRIBUTION|PROCESSING|SORTING|SORT)\\b|\\b(?:NDC|P&DC|ISC)\\b");
  function isUSLocation(loc) {
    var s = clean(stripCJK(loc));
    if (!s) return false;
    if (US_COUNTRY_RE.test(s)) return true;
    if (US_STATE_TAIL_RE.test(s.toUpperCase() === s ? s : s.replace(/,\s*([a-z]{2})\s*$/i, function (m, st) { return ", " + st.toUpperCase(); }))) return true;
    if (US_STATE_ZIP_RE.test(s)) return true;
    if (US_FACILITY_RE.test(s)) return true;
    var tail = s.split(",").pop().trim().toLowerCase();
    return s.indexOf(",") !== -1 && US_STATE_NAMES.indexOf(tail) !== -1;
  }

  /* Carrier display name in English: alias table first (Chinese carrier names), then the carrier map, then the name
     with CJK stripped; "" when nothing English is left. */
  function carrierName(c, name) {
    var raw = clean(name);
    if (!raw) return "";
    var aliases = Object.keys(c.carrierAliases);
    for (var i = 0; i < aliases.length; i++) if (raw.indexOf(aliases[i]) !== -1) return c.carrierAliases[aliases[i]];
    if (!CJK.test(raw)) return raw;
    var key = raw.toLowerCase();
    var keys = Object.keys(c.carriers);
    for (var k = 0; k < keys.length; k++) if (key.indexOf(keys[k]) !== -1) return c.carriers[keys[k]].name;
    return clean(stripCJK(raw));
  }

  /* Carrier alias (carrier_display_name): every carrier name the page shows becomes the alias. The real name is still
     used internally to find the carrier's tracking link. With no alias set, names pass through unchanged. */
  function carrierNameRe(rules) {
    var names = [];
    var cs = rules.carriers || {};
    Object.keys(cs).forEach(function (k) { if (cs[k].name) names.push(cs[k].name); });
    var al = rules.carrier_aliases || {};
    Object.keys(al).forEach(function (k) { names.push(al[k]); });
    /* Case-sensitive (as written, or all caps as carriers often print it) so "UPS" never hits words like "pick-ups". */
    names = names.concat(names.map(function (n) { return n.toUpperCase(); }));
    names = names.filter(function (n) { return n && !CJK.test(n); }).sort(function (a, b) { return b.length - a.length; });
    if (!names.length) return null;
    return new RegExp("\\b(?:" + names.map(function (n) { return n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }).join("|") + ")\\b", "g");
  }
  function displayCarrier(c, name) { return c.carrierDisplay ? (name ? c.carrierDisplay : "") : name; }
  function hideCarrierNames(c, text) {
    if (!c.carrierDisplay || !text || !c.carrierNameRe) return text;
    return String(text).replace(c.carrierNameRe, c.carrierDisplay);
  }

  /* The carrier's own English wording for an event: 17TRACK's English translation, else the raw text when it has no
     CJK characters, else "" (the page then hides the toggle for that event). */
  function carrierWording(original, translated) {
    if (translated && !CJK.test(translated)) return translated;
    if (original && !CJK.test(original)) return original;
    return "";
  }

  /* One raw event -> { time, message, step, kind, leg, location, carrierText, carrierLocation, carrier, rewritten }.
     Raw Chinese text is read for matching only and is not returned. */
  function classify(c, ev) {
    var original = clean(ev.text || ev.description || "");
    var translated = clean(ev.translation || "");
    var rawLoc = clean(ev.location);
    var wording = carrierWording(original, translated);
    var rule = matchRule(c, original) || (translated ? matchRule(c, translated) : null);
    var step = null, kind = "ok", message, leg = null, rewritten = false;
    if (rule) {
      message = rule.message; kind = rule.kind; step = rule.step; leg = rule.leg; rewritten = true;
    } else {
      message = wording || genericLine(ev, CJK.test(rawLoc));
      if (ev.stage && Object.prototype.hasOwnProperty.call(c.stageSteps, ev.stage)) step = c.stageSteps[ev.stage];
      if (/^(Returning|Returned)$/.test(ev.stage || "") || /^(DeliveryFailure|Exception)/.test(ev.subStatus || "")) kind = "exception";
    }
    var location = friendlyLocation(c, rawLoc);
    return {
      time: ev.time || null, message: stripCJK(message) || GENERIC_NONE, step: step, stepIndex: step == null ? -1 : c.idx[step], kind: kind, leg: leg,
      location: hideCarrierNames(c, location), carrierText: hideCarrierNames(c, wording),
      carrierLocation: hideCarrierNames(c, CJK.test(rawLoc) ? location : rawLoc),
      carrier: displayCarrier(c, carrierName(c, ev.carrier)), headline: rule ? rule.headline : null, rewritten: rewritten, stage: ev.stage || null,
    };
  }

  function carrierInfo(c, name, number) {
    var key = carrierName(c, name).toLowerCase();
    var keys = Object.keys(c.carriers);
    for (var i = 0; i < keys.length; i++) {
      if (key.indexOf(keys[i]) !== -1) {
        var ent = c.carriers[keys[i]];
        return { name: c.carrierDisplay || ent.name, number: number || "", url: number ? ent.url.replace("{number}", encodeURIComponent(number)) : "" };
      }
    }
    return { name: c.carrierDisplay || carrierName(c, name) || "Carrier", number: number || "", url: "" };
  }

  function addDays(iso, days) { return new Date(new Date(iso).getTime() + days * DAY).toISOString(); }
  function addBusinessDays(iso, days) {
    var d = new Date(iso), n = 0;
    while (n < days) { d = new Date(d.getTime() + DAY); var wd = d.getUTCDay(); if (wd !== 0 && wd !== 6) n++; }
    return d.toISOString();
  }
  function businessDaysBetween(a, b) {
    var d = new Date(a), end = new Date(b), n = 0;
    while (d.getTime() + DAY <= end.getTime()) { d = new Date(d.getTime() + DAY); var wd = d.getUTCDay(); if (wd !== 0 && wd !== 6) n++; }
    return n;
  }

  /* order: { number, placed (ISO), cancelled (bool), fulfillments: [{ created (ISO), carrier, number, url }] }
     tracks: [{ carrier, number, status (17TRACK main status or null), events: [{ time, text, translation, location, stage, subStatus, carrier }] }]
     shipping: { processing_days: [min, max], delivery_days: [min, max] }
     opts: { now (ISO), gapDays (default 3), staleDays (default 7), staleInternationalDays (default 10) } */
  function buildResult(c, order, tracks, shipping, opts) {
    opts = opts || {};
    var now = opts.now ? new Date(opts.now) : new Date();
    var gapDays = opts.gapDays == null ? 3 : opts.gapDays;
    var staleDays = opts.staleDays == null ? 7 : opts.staleDays;
    var staleIntl = opts.staleInternationalDays == null ? 10 : opts.staleInternationalDays;
    var proc = (shipping && shipping.processing_days) || [2, 4];
    var deliv = (shipping && shipping.delivery_days) || [8, 15];
    var fulfillments = (order && order.fulfillments) || [];

    var events = [];
    (tracks || []).forEach(function (t) {
      (t.events || []).forEach(function (ev) {
        var e = classify(c, Object.assign({ carrier: t.carrier }, ev));
        events.push(e);
      });
    });
    events.sort(function (a, b) { return new Date(a.time || 0) - new Date(b.time || 0); });

    var reached = 0; // confirmed: the order exists
    if (fulfillments.length || events.length) reached = Math.max(reached, c.idx.processing);
    else if (order && !order.cancelled) reached = Math.max(reached, c.idx.processing);
    var lastLeg = null, lastOk = null;
    events.forEach(function (e) {
      if (e.kind === "ok" && e.stepIndex > reached) reached = e.stepIndex;
      if (e.kind === "ok") { lastOk = e; if (e.leg) lastLeg = e.leg; }
    });
    var latest = events.length ? events[events.length - 1] : null;
    var delivered = events.filter(function (e) { return e.step === "delivered"; }).pop() || null;
    var exception = latest && latest.kind === "exception" ? latest : null;
    var lateReturn = events.length && events[events.length - 1].kind === "exception";

    /* Ship date: first carrier pickup scan, else the fulfillment time, else an estimate from the order date. */
    var firstPickup = null;
    for (var i = 0; i < events.length; i++) if (events[i].kind === "ok" && events[i].stepIndex >= c.idx.shipped) { firstPickup = events[i].time; break; }
    var shipDate = firstPickup || (fulfillments.length ? fulfillments[0].created : null);
    var shipEstimated = false, shipFrom = shipDate;
    if (!shipDate && order && order.placed) { shipFrom = addBusinessDays(order.placed, proc[0]); shipDate = addBusinessDays(order.placed, proc[1]); shipEstimated = true; }

    var eta = null;
    if (shipDate && !delivered && !(order && order.cancelled)) {
      eta = { from: addDays(shipFrom, deliv[0]), to: addDays(shipDate, deliv[1]), basis: shipEstimated ? "order" : "ship" };
    }
    var late = !!(eta && now.getTime() > new Date(eta.to).getTime());

    /* Gap notice: real silence, stated as silence. */
    var notice = null;
    var daysSince = latest ? Math.floor((now - new Date(latest.time)) / DAY) : null;
    if (order && order.cancelled) {
      notice = { kind: "cancelled", text: "This order was cancelled. If that is unexpected, contact us." };
    } else if (!fulfillments.length && !events.length) {
      notice = { kind: "preparing", text: "Your order is confirmed and being prepared. Tracking appears here once it ships, usually within " + proc[0] + " to " + proc[1] + " business days." };
    } else if (!events.length) {
      var bd = businessDaysBetween(fulfillments[0].created, now.toISOString());
      notice = bd > 5
        ? { kind: "no_scan_long", text: "We cannot see carrier scans for this label yet. Contact us and we will check with the carrier." }
        : { kind: "label", text: "Label created. Carriers usually show the first scan within 1 to 3 business days." };
    } else if (delivered) {
      notice = null;
    } else if (exception) {
      notice = { kind: "exception", text: exception.message };
    } else if (daysSince != null && daysSince >= gapDays) {
      var since = latest.time;
      var intl = lastLeg === "international" || (lastOk && lastOk.leg === "international");
      if (daysSince >= (intl ? staleIntl : staleDays)) {
        notice = { kind: "delayed", since: since, text: "No new scan since [date]. That is longer than usual. Contact us and we will check with the carrier." };
      } else if (intl) {
        notice = { kind: "gap", since: since, text: "No new scans since [date]. Packages can go a few days between scans while in transit. We are keeping an eye on it." };
      } else {
        notice = { kind: "gap", since: since, text: "No new scan since [date]. We will update this page as soon as the carrier does." };
      }
    }
    if (late && !delivered && !(notice && (notice.kind === "delayed" || notice.kind === "exception"))) {
      notice = { kind: "late", text: "Running late against our estimate. We are checking with the carrier. Contact us if you need anything in the meantime." };
    }

    var current = delivered ? c.idx.delivered : reached;
    var steps = c.steps.map(function (key, i) {
      var evidence = null;
      if (i === c.idx.confirmed) evidence = order && order.placed;
      else if (i === c.idx.processing) evidence = order && order.placed;
      else { for (var k = 0; k < events.length; k++) if (events[k].kind === "ok" && events[k].stepIndex === i) { evidence = events[k].time; break; } }
      return { key: key, label: c.labels[key] || key, done: i < current, current: i === current, date: i <= current ? evidence : null };
    });

    var status;
    if (order && order.cancelled) status = "cancelled";
    else if (delivered) status = "delivered";
    else if (exception) status = "exception";
    else if (notice && notice.kind === "delayed") status = "delayed";
    else if (late) status = "late";
    else status = c.steps[current];

    var headline;
    if (status === "cancelled") headline = "Order cancelled";
    else if (delivered) headline = "Delivered";
    else if (exception) headline = exception.message.replace(/\.$/, "");
    else if (!events.length) headline = fulfillments.length ? "Label created, waiting for the first scan" : "Confirmed and being prepared";
    else headline = ((lastOk || latest).headline || (lastOk || latest).message).replace(/\.$/, "");

    var carriers = [];
    var seen = {};
    (tracks || []).forEach(function (t) { if (t.number && !seen[t.number]) { seen[t.number] = 1; carriers.push(carrierInfo(c, t.carrier, t.number)); } });
    fulfillments.forEach(function (f) { if (f.number && !seen[f.number]) { seen[f.number] = 1; var ci = carrierInfo(c, f.carrier, f.number); if (!ci.url && f.url) ci.url = f.url; carriers.push(ci); } });

    return scrub({
      status: status, headline: headline, stepIndex: current, steps: steps,
      latest: latest ? { time: latest.time, message: latest.message, carrierText: latest.carrierText, location: latest.location, carrierLocation: latest.carrierLocation } : null,
      daysSinceUpdate: daysSince, notice: notice, eta: eta, late: late,
      deliveredAt: delivered ? delivered.time : null, shipDate: shipDate, shipEstimated: shipEstimated,
      exception: exception ? { message: exception.message, carrierText: exception.carrierText, time: exception.time } : null,
      events: events.slice().reverse(), carriers: carriers, lastLeg: lastLeg, returning: !!lateReturn && /return/i.test(latest.message),
    });
  }

  return { compile: compile, classify: classify, matchRule: matchRule, friendlyLocation: friendlyLocation, carrierInfo: carrierInfo,
           carrierName: carrierName, carrierWording: carrierWording, genericLine: genericLine, hasCJK: hasCJK, stripCJK: stripCJK, scrub: scrub,
           isUSLocation: isUSLocation, buildResult: buildResult, addDays: addDays, addBusinessDays: addBusinessDays, STEP_LABELS: STEP_LABELS };
});
